import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Quote, Star, Filter, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TestimonialsPage = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [filter, setFilter] = useState('All');
  const [loading, setLoading] = useState(true);
  const [featuredIndex, setFeaturedIndex] = useState(0);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/testimonials`);
        setTestimonials(data);
      } catch (error) {
        console.error('Error fetching testimonials:', error);
        // Fallback data
        setTestimonials([
          {
            id: 't1',
            quote: 'Luminarc™ transformed how our leadership team approaches complex decisions. The narrative thinking framework gave us clarity we didn\'t know we needed. It\'s not just training—it\'s a paradigm shift.',
            service_tag: 'Luminarc',
            rating: 5,
          },
          {
            id: 't2',
            quote: 'The consulting engagement delivered measurable results within 90 days. Our operational efficiency improved by 40%, and we identified new revenue streams we hadn\'t considered.',
            service_tag: 'Consulting',
            rating: 5,
          },
          {
            id: 't3',
            quote: 'Expertize Edge\'s corporate training programs are unmatched. Our team engagement scores increased by 35% post-training, and we\'ve seen lasting behavioral change.',
            service_tag: 'Corporate Training',
            rating: 5,
          },
          {
            id: 't4',
            quote: 'The executive coaching program helped me find my authentic leadership voice. I\'m now more confident in leading my team through uncertainty and making tough calls.',
            service_tag: 'Coaching',
            rating: 5,
          },
          {
            id: 't5',
            quote: 'We\'ve partnered with Expertize Edge for three years. Their consistent quality and deep understanding of our culture makes them invaluable partners in our growth.',
            service_tag: 'Corporate Training',
            rating: 5,
          },
          {
            id: 't6',
            quote: 'The strategic consulting engagement reshaped our entire go-to-market approach. Revenue is up 60% year over year, and we have a clear roadmap for the future.',
            service_tag: 'Consulting',
            rating: 5,
          },
        ]);
      } finally {
        setLoading(false);
      }
    };
    fetchTestimonials();
  }, []);

  const filters = ['All', 'Luminarc', 'Corporate Training', 'Consulting', 'Coaching'];

  const filteredTestimonials = filter === 'All'
    ? testimonials
    : testimonials.filter(t => t.service_tag === filter);

  const featuredTestimonials = testimonials.slice(0, 3);

  const nextFeatured = () => {
    setFeaturedIndex((prev) => (prev + 1) % featuredTestimonials.length);
  };

  const prevFeatured = () => {
    setFeaturedIndex((prev) => (prev - 1 + featuredTestimonials.length) % featuredTestimonials.length);
  };

  return (
    <main className="pt-20">
      {/* Hero */}
      <section className="relative py-32 bg-brand-charcoal overflow-hidden" data-testid="testimonials-hero">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(92,45,145,0.3),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(196,158,77,0.2),transparent_50%)]" />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <span className="inline-flex items-center gap-2 bg-brand-gold/20 border border-brand-gold/30 rounded-full px-4 py-2 mb-6">
                <Star className="w-4 h-4 text-brand-gold" />
                <span className="text-brand-gold text-sm font-medium">Client Success Stories</span>
              </span>
              
              <h1 className="font-heading text-5xl sm:text-6xl font-bold text-white leading-tight mb-6">
                What Our{' '}
                <span className="text-brand-gold">Clients</span>{' '}
                Say
              </h1>
              
              <p className="text-xl text-gray-300 leading-relaxed mb-8">
                Don't just take our word for it. Here's what leaders and organizations 
                have experienced working with Expertize Edge.
              </p>
              
              {/* Stats */}
              <div className="flex flex-wrap gap-8">
                {[
                  { value: '95%', label: 'Would Recommend' },
                  { value: '4.9/5', label: 'Average Rating' },
                  { value: '500+', label: 'Success Stories' },
                ].map((stat) => (
                  <div key={stat.label} className="text-center">
                    <p className="text-3xl font-bold text-brand-gold">{stat.value}</p>
                    <p className="text-gray-400 text-sm">{stat.label}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Featured Testimonial Carousel */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="relative"
            >
              {featuredTestimonials.length > 0 && (
                <div className="relative">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={featuredIndex}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8"
                    >
                      <Quote className="w-10 h-10 text-brand-gold/40 mb-4" />
                      <p className="text-white text-lg italic leading-relaxed mb-6">
                        "{featuredTestimonials[featuredIndex]?.quote}"
                      </p>
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-brand-purple to-brand-gold flex items-center justify-center border-2 border-brand-gold">
                          <Star className="w-6 h-6 text-white fill-white" />
                        </div>
                        <div>
                          <p className="text-white font-semibold">Verified Client</p>
                        </div>
                      </div>
                      <div className="flex gap-1 mt-4">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star key={star} className="w-5 h-5 text-brand-gold fill-brand-gold" />
                        ))}
                      </div>
                    </motion.div>
                  </AnimatePresence>
                  
                  {/* Navigation */}
                  <div className="flex justify-center gap-4 mt-6">
                    <button
                      onClick={prevFeatured}
                      className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white hover:bg-white/10 transition-colors"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <div className="flex items-center gap-2">
                      {featuredTestimonials.map((_, idx) => (
                        <button
                          key={idx}
                          onClick={() => setFeaturedIndex(idx)}
                          className={`w-2 h-2 rounded-full transition-all ${
                            idx === featuredIndex ? 'bg-brand-gold w-6' : 'bg-white/30'
                          }`}
                        />
                      ))}
                    </div>
                    <button
                      onClick={nextFeatured}
                      className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white hover:bg-white/10 transition-colors"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Filter Bar */}
      <section className="py-8 bg-white border-b border-brand-lavender sticky top-20 z-40" data-testid="testimonials-filter">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <Filter className="w-5 h-5 text-brand-muted mr-2" />
            {filters.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-5 py-2.5 font-medium rounded-full transition-all duration-200 ${
                  filter === f
                    ? 'bg-brand-purple text-white shadow-lg shadow-brand-purple/25'
                    : 'bg-brand-lavender text-brand-body hover:bg-brand-purple/10'
                }`}
                data-testid={`filter-${f.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="py-24 bg-brand-bg-light" data-testid="testimonials-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-brand-purple border-t-transparent rounded-full mx-auto"></div>
            </div>
          ) : (
            <>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center mb-12"
              >
                <h2 className="font-heading text-3xl font-bold text-brand-charcoal mb-4">
                  All <span className="text-brand-purple">Testimonials</span>
                </h2>
                <p className="text-brand-muted">
                  {filteredTestimonials.length} success {filteredTestimonials.length === 1 ? 'story' : 'stories'} 
                  {filter !== 'All' && ` in ${filter}`}
                </p>
              </motion.div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                <AnimatePresence mode="popLayout">
                  {filteredTestimonials.map((testimonial, index) => (
                    <motion.div
                      key={testimonial.id}
                      layout
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ delay: index * 0.05 }}
                      className="group"
                      data-testid={`testimonial-card-${index}`}
                    >
                      <div className="bg-white rounded-2xl p-8 h-full border border-transparent hover:border-brand-purple/20 hover:shadow-xl transition-all duration-300 relative overflow-hidden">
                        {/* Hover gradient */}
                        <div className="absolute inset-0 bg-gradient-to-br from-brand-purple/5 to-brand-gold/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        
                        <div className="relative">
                          <Quote className="w-10 h-10 text-brand-gold/20 mb-4" />
                          
                          {/* Rating */}
                          <div className="flex gap-1 mb-4">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star key={star} className="w-4 h-4 text-brand-gold fill-brand-gold" />
                            ))}
                          </div>
                          
                          <p className="text-brand-body leading-relaxed mb-6 italic">
                            "{testimonial.quote}"
                          </p>
                          
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-brand-purple to-brand-gold flex items-center justify-center border-2 border-brand-lavender group-hover:border-brand-purple transition-colors">
                              <Star className="w-5 h-5 text-white fill-white" />
                            </div>
                            <div>
                              <p className="font-semibold text-brand-charcoal">Verified Client</p>
                            </div>
                          </div>
                          
                          {testimonial.service_tag && (
                            <span className="inline-block mt-4 px-3 py-1 bg-brand-lavender text-brand-purple text-xs font-semibold rounded-full">
                              {testimonial.service_tag}
                            </span>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-br from-brand-purple to-brand-charcoal relative overflow-hidden" data-testid="testimonials-cta">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px',
          }} />
        </div>
        
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-6">
              Ready to Write Your Success Story?
            </h2>
            <p className="text-white/80 text-xl mb-10 max-w-2xl mx-auto">
              Join hundreds of leaders who have transformed their organizations with Expertize Edge.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-brand-gold text-brand-charcoal font-semibold px-10 py-4 hover:bg-brand-gold-lite hover:scale-105 transition-all duration-300 group"
              data-testid="testimonials-cta-button"
            >
              Start Your Journey
              <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default TestimonialsPage;
